import { EventObject, Typestate } from 'xstate';
import {
  AcknowledgeLeaseTermsError,
  AutopayErrors,
  BankAccountInput,
  CartItemValidation,
  CartValidation,
  DebitDetailsInput,
  GenerateContractErrors,
  GetExistingLeaseDocumentsErrors,
  LeaseDocuments,
  LeaseItem,
  ProtectionPlusEnrollmentError,
  RemoveVirtualCardErrors,
  SelectLeaseOptionErrors,
  SigningErrors,
  UpdateBankAccountErrors,
  UpdateDebitErrors,
  UpdateEmploymentErrors,
  UpdateOrderErrors,
} from '../../gql/graphql';
import { SupportedLanguage } from '../../helpers/language/language.helper';
import { PayFrequencyModel } from '../../networkRequests/employment/updateEmployment';
import { EmbedUrlError } from '../../networkRequests/virtualCard/getEmbedURL';
import { PaymentSelectionErrors } from '../../pages/paymentSelectionError/PaymentSelectionError';
import { ApplyRequestMachineContext, ApplyResponseMachineContext } from './shared/context/applyMachineContext';
import { CustomerSearchResponseMachineContext } from './shared/context/customerSearchMachineContext';
import { LoanMachineContext } from './shared/context/loanMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { RoleMachineContext } from './shared/context/roleMachineContext';

// Context Types
export interface ExistingApplicationMachineContext
  extends RoleMachineContext,
    OrderIdMachineContext,
    OrderMachineContext,
    ApplyRequestMachineContext,
    ApplyResponseMachineContext, // forwarded from other machiness
    CustomerSearchResponseMachineContext, // forwarded from other machiness
    LoanMachineContext {
  // approvalAmount?: string; // for virtual card

  highestSupportedApplicationMilestone: number;

  skipApproval: boolean;
  canGoBackViaHeader: boolean;
  skipToPage?: string | null;
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;

  bankDetails: BankAccountInput;
  updateBankAccountErr?: UpdateBankAccountErrors;
  plaidToken: {
    linkToken: string;
    publicToken: string;
  };
  bankConnection: {
    plaidLoading: boolean; // TODO: emily, this doesn't have any assignments to it, do you wanna try the tag solution?
    paymentSelectionError?: PaymentSelectionErrors;
    isTokenizedAcct: boolean;
    bankAcctLast4: string;
  };
  updateEmploymentErrors: UpdateEmploymentErrors[];
  payFrequencyDetails: PayFrequencyModel;

  selectedLeaseTerm?: string; // comes from the selectLeaseOption resp. This is NOT defaulted to the selectedLeaseTerm application property
  customerSelectedLeaseTerm?: string;
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  acknowledgeLeaseTermsErrors?: AcknowledgeLeaseTermsError[];

  leaseDocuments?: LeaseDocuments;
  leaseDocumentsLanguage?: SupportedLanguage;
  leaseDocumentsByLanguage?: Partial<Record<SupportedLanguage, LeaseDocuments>>;
  generateContractErrors: GenerateContractErrors[];
  getExistingLeaseDocumentsErrors: GetExistingLeaseDocumentsErrors[];
  signingErrors: SigningErrors[];
  ipAddress: string;
  leaseSigned: boolean;
  loanSigned: boolean;
  cardExpiration: number;
  embedUrl: string;
  embedUrlError: EmbedUrlError | null;
  removeVirtualCardErrors: RemoveVirtualCardErrors[];

  cart: CartItemModel[];
  cartTotal: number; // represents the user inputted cart total
  itemToEdit: EditCartItemModel | null;
  debitInfo: DebitDetailsInput;
  updateDebitInfoErrors: UpdateDebitErrors[];

  updateOrderErrors?: UpdateOrderErrors[];
  cartValidationErrors?: CartValidation[]; // cart level errors
  cartValidationItemErrors?: Record<string, CartItemValidation>; // item level warnings
  cartValidationItemWarnings?: Record<string, CartItemValidation>; // item and cart level warnings?
  autoremovedCartItemNames?: string[];

  autopayErrors?: AutopayErrors[];

  fromContractSummary: boolean;

  applicationItems: LeaseItem[]; // make sure this is populated before you use it

  showNotAcknowledgedModal?: boolean;
  protectionPlusEnrollmentErrors?: ProtectionPlusEnrollmentError[];
}

// State Types
export enum ExistingApplicationStates {
  LoanApplication = 'LoanApplication',
  PaymentSchedule = 'PaymentSchedule',
  AutoPayEnrollment = 'AutoPayEnrollment',
  FinalApproval = 'FinalApproval',
  CartValidation = 'CartValidation',
  ApplicationExists = 'ApplicationExists',
  LeaseApplication = 'LeaseApplication',
  ContractSummaryEcomm = 'ContractSummaryEcomm',
  ContractSummaryBM = 'ContractSummaryBM',
  LeaseAgreement = 'LeaseAgreement',
  DebitCardEntry = 'DebitCardEntry',
  DebitCardError = 'DebitCardError',
  DebitCardHardDecline = 'DebitCardHardDecline',
  DebitCardSoftDecline = 'DebitCardSoftDecline',
  DebitCardFatalError = 'DebitCardFatalError',
  BankAccountError = 'BankAccountError',
  BankAccountHardDecline = 'BankAccountHardDecline',
  Loading = 'Loading',
  VirtualCardApplication = 'VirtualCardApplication',
  PayFrequency = 'PayFrequency',
  PayFrequencyInit = 'PayFrequencyInit',
  UpdateEmployment = 'UpdateEmployment',
  RefreshAfterEmployment = 'RefreshAfterEmployment',
  RouteAfterEmployment = 'RouteAfterEmployment',
  BankDetails = 'BankDetails',
  Approval = 'Approval',
  Prequalification = 'Prequalification',
  ItemEntry = 'ItemEntry',
  CartSummary = 'CartSummary',
  CartTotal = 'CartTotal',
  CartReviewLoading = 'CartReviewLoading',
  CartError = 'CartError',
  VirtualCardPage = 'VirtualCardPage',
  LeaseDecline = 'LeaseDecline',
  LoanDecline = 'LoanDecline',
  Signed = 'Signed',
  CheckLeaseStatus = 'CheckLeaseStatus',
  ApprovalConfirmation = 'ApprovalConfirmation',
  PaymentSelection = 'PaymentSelection',
  PaymentSelectionError = 'PaymentSelectionError',
  PlaidModal = 'PlaidModal',
  PaymentReview = 'PaymentReview',
  FetchLinkToken = 'FetchLinkToken',
  UpdateBankAccount = 'UpdateBankAccount',
  InternalError = 'InternalError',
}

export interface ExistingApplicationMachineStatesSchema {
  [ExistingApplicationStates.LoanApplication]: {};
  [ExistingApplicationStates.FinalApproval]: {};
  [ExistingApplicationStates.AutoPayEnrollment]: {};
  [ExistingApplicationStates.ApplicationExists]: {};
  [ExistingApplicationStates.LeaseApplication]: {};
  [ExistingApplicationStates.ContractSummaryEcomm]: {};
  [ExistingApplicationStates.Loading]: {};
  [ExistingApplicationStates.LeaseAgreement]: {};
  [ExistingApplicationStates.DebitCardEntry]: {};
  [ExistingApplicationStates.DebitCardError]: {};
  [ExistingApplicationStates.DebitCardHardDecline]: {};
  [ExistingApplicationStates.DebitCardSoftDecline]: {};
  [ExistingApplicationStates.DebitCardFatalError]: {};
  [ExistingApplicationStates.BankAccountError]: {};
  [ExistingApplicationStates.BankAccountHardDecline]: {};
  [ExistingApplicationStates.Loading]: {};
  [ExistingApplicationStates.VirtualCardApplication]: {};
  [ExistingApplicationStates.PayFrequency]: {};
  [ExistingApplicationStates.PayFrequencyInit]: {};
  [ExistingApplicationStates.UpdateEmployment]: {};
  [ExistingApplicationStates.RefreshAfterEmployment]: {};
  [ExistingApplicationStates.RouteAfterEmployment]: {};
  [ExistingApplicationStates.BankDetails]: {};
  [ExistingApplicationStates.Prequalification]: {};
  [ExistingApplicationStates.Approval]: {};
  [ExistingApplicationStates.ItemEntry]: {};
  [ExistingApplicationStates.CartSummary]: {};
  [ExistingApplicationStates.CartTotal]: {};
  [ExistingApplicationStates.CartError]: {};
  [ExistingApplicationStates.CartReviewLoading]: {};
  [ExistingApplicationStates.VirtualCardPage]: {};
  [ExistingApplicationStates.CheckLeaseStatus]: {};
  [ExistingApplicationStates.ApprovalConfirmation]: {};
  [ExistingApplicationStates.PaymentSelection]: {};
  [ExistingApplicationStates.PaymentSelectionError]: {};
  [ExistingApplicationStates.PaymentReview]: {};
  [ExistingApplicationStates.PlaidModal]: {};
  [ExistingApplicationStates.FetchLinkToken]: {};
  [ExistingApplicationStates.UpdateBankAccount]: {};
  [ExistingApplicationStates.InternalError]: {};
}

export interface ExistingApplicationMachineStates extends Typestate<ExistingApplicationMachineContext> {
  type: ExistingApplicationMachineStatesSchema;
}

// Event Types
export enum ExistingApplicationEvents {
  NEXT = 'NEXT',
  BACK = 'BACK',
  SKIP = 'SKIP', // for skipping autopay
  RETRY = 'RETRY',
  ERROR = 'ERROR',
  OTB = 'OTB',
  EDIT_DEBIT_INFO = 'EditDebitInfo',
  UPDATE_DEBIT_CARD = 'UpdateDebitCard',
  SELECT_PLAID = 'SelectPlaid',
  SELECT_MANUAL = 'SelectManual',
  FETCH_LINK_TOKEN = 'FetchLinkToken',
  PLAID_MODAL_LOADING = 'PlaidModalLoading',
  UPDATE_BANK_ACCOUNT = 'UpdateBankAccount',
  UPDATE_EMPLOYMENT = 'UpdateEmployment',
  REMOVE_VIRTUAL_CARD = 'RemoveVirtualCard',
  EDIT_CART_ITEM = 'EditCartItem',
  EDIT_CART = 'EditCart',
  DELETE_CART_ITEM = 'DeleteCartItem',
  SIGN_AGREEMENT = 'SignAgreement',
  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',
  // ContractSummaryBM OOBA Resend events
  REFRESH_CONTRACT_SUMMARY_STATUS = 'RefreshContractSummaryStatus',
  CLOSE_NOT_ACKNOWLEDGED_MODAL = 'CloseNotAcknowledgedModal',
  // LeaseAgreement SignalR events
  REFRESH_SIGNING_STATUS = 'RefreshSigningStatus',
  LANGUAGE_CHANGED = 'LanguageChanged',
}

type ExistingApplicationEventsTypeSchema =
  | ExistingApplicationEvents.NEXT
  | ExistingApplicationEvents.BACK
  | ExistingApplicationEvents.SKIP
  | ExistingApplicationEvents.RETRY
  | ExistingApplicationEvents.ERROR
  | ExistingApplicationEvents.EDIT_DEBIT_INFO
  | ExistingApplicationEvents.OTB
  | ExistingApplicationEvents.SELECT_PLAID
  | ExistingApplicationEvents.SELECT_MANUAL
  | ExistingApplicationEvents.FETCH_LINK_TOKEN
  | ExistingApplicationEvents.PLAID_MODAL_LOADING
  | ExistingApplicationEvents.UPDATE_BANK_ACCOUNT
  | ExistingApplicationEvents.UPDATE_EMPLOYMENT
  | ExistingApplicationEvents.REMOVE_VIRTUAL_CARD
  | ExistingApplicationEvents.EDIT_CART_ITEM
  | ExistingApplicationEvents.EDIT_CART
  | ExistingApplicationEvents.DELETE_CART_ITEM
  | ExistingApplicationEvents.SIGN_AGREEMENT
  | ExistingApplicationEvents.UPDATE_DEBIT_CARD
  | ExistingApplicationEvents.UPDATE_EMPLOYMENT
  | ExistingApplicationEvents.TRACK
  | ExistingApplicationEvents.PAGE
  | ExistingApplicationEvents.IDENTIFY
  | ExistingApplicationEvents.REFRESH_CONTRACT_SUMMARY_STATUS
  | ExistingApplicationEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL
  | ExistingApplicationEvents.REFRESH_SIGNING_STATUS
  | ExistingApplicationEvents.LANGUAGE_CHANGED;

export interface ExistingApplicationMachineEvents extends EventObject {
  type: ExistingApplicationEventsTypeSchema;
  data?: any;
}

// Guard Types
export enum ExistingApplicationGuardsType {
  APP_MODE_LEASE = 'AppModeLease',
  APP_MODE_LOAN = 'AppModeLoan',
}
type ExistingApplicationGuardsTypeSchema = ExistingApplicationGuardsType.APP_MODE_LEASE | ExistingApplicationGuardsType.APP_MODE_LOAN;

export interface ExistingApplicationMachineGuards extends EventObject {
  type: ExistingApplicationGuardsTypeSchema;
}

// TODO: these should live somewhere else
export type CartItemModel = {
  itemName: string;
  price: number;
  quantity: number;
  id: number;
};

export const nextId = (cart: CartItemModel[]): number => {
  if (!cart.length || !cart.at(-1)) return 0;

  // since the ids should always be ascending, 1 + the last will be the next highest integer
  return cart.at(-1)!.id + 1;
};

export type EditCartItemModel = {
  index: number;
  item: CartItemModel;
};
